/// styles.ts
import styled from "styled-components";

// ===========================
// Component Props Interfaces
// ===========================
interface CalendarLengthButtonProps {
  selected?: boolean;
}

// ===========================
// Button Components
// ===========================
export const CalendarLengthButton = styled.button<CalendarLengthButtonProps>`
  /* Base styles */
  background-color: ${({ selected }) => (selected ? "#ff6a0c" : "#cccccc")};
  color: ${({ selected }) => (selected ? "white" : "#666")};
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: none;
  
  /* Typography */
  font-size: 12px;
  line-height: 12px;
  font-weight: bold;
  
  /* Sizing */
  padding: 1rem 4rem;
  margin-right: 1rem;
  min-width: unset;
  width: auto;
  height: 40px;

  /* States */
  &:hover {
    box-shadow: none;
  }

  &.active {
    background-color: #cc4e00;
  }

  /* Mobile styles */
  @media (max-width: 768px) {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    margin-right: 0;
    padding: 1rem 0;
    font-size: 11px;
    line-height: 11px;
    height: 30px;
  }
`;

export const CalendarLengthButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 0;

  @media (max-width: 768px) {
    gap: 0.5rem;
    height: 28px;

    .calendar-length-button {
      min-width: 0;
      width: 100%;
      margin-right: 0;
      flex: 1 1 0;
    }

    .grouping-individual,
    .grouping-group {
      flex: 0.8 1 0;
      max-width: 140px;
      white-space: nowrap;
    }

    .grouping-favorites {
      flex: 2 1 0;
      min-width: 120px;
      max-width: 100%;
      white-space: nowrap;
    }
  }
`;

// ===========================
// Schedule Components
// ===========================
export const StyledSchedule = styled.div`
  position: relative;
`;

export const ScheduleHeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ScheduleHeaderButtons = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
    width: 100%;
    gap: 0.5rem;

    > div {
      flex: 1 1 0;
    }

    .MuiButton-root {
      width: 100% !important;
      box-shadow: none !important;
      margin-right: 0 !important;
    }
  }
`;

export const ScheduleTitle = styled.span`
  font-size: 4rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// ===========================
// Calendar Component
// ===========================
export const StyledAppCalendar = styled.div<{ $span?: string, $grouping?: string }>`
  /* Base styles */
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;

  /* Row mode specific */
  &.row-mode {
    min-height: 150px !important;
    min-width: 1000px; // IMPORTANT: this makes header in group weekly stretch full width
    max-height: fit-content !important; // IMPORTANT: this makes cell in group weekly show all events

    .rbc-month-view .rbc-month-row:not(.current-week) {
      display: none;
      min-width: 650px;
    }

    .rbc-month-view .rbc-month-row.current-week {
      height: 150px;
      border-top:none;
    }

    .rbc-addons-dnd.rbc-calendar {
      height: auto;
    }

    .rbc-date-cell {
      display:none;
    }

  }

  /* ===========================
     Calendar Base Structure
  =========================== */
  .rbc-calendar {
    width: 100%;
    max-width: 100%;
  }

  /* ===========================
     View-specific Styles
  =========================== */
  
  /* Month View */
  .rbc-month-view {
    max-width: 100%;
    min-width: unset;
  }

  .rbc-month-view-short {
    height: unset;
  }

  .rbc-month-row {
    flex: 1 1 0%;
    min-height: fit-content !important;
    height: fit-content !important;
    overflow:unset !important;
  }

  /* Time View */
  .rbc-time-view {
    min-width: ${({ $span }) => $span === 'day' ? 'fit-content' : '100%'};
    min-height: unset !important;
    
  }



  .rbc-time-content {
   overflow: unset !important;
  }

  .rbc-time-view.rbc-time-view-resources {
    /*width: fit-content !important;*/
  }

  /* ===========================
     Headers
  =========================== */
  .rbc-header {
    background-color: #e4e4e4 !important;
    font-weight: normal;
    text-align: center;
    padding: 0;
    display:flex;
    height:25px;
    justify-content:center;
    align-items:center;
    font-weight:bold;
    border-right:1px solid white;
    border-left:1px solid #bbb;
  }

  .rbc-time-header-cell {
    min-height:25px;
    background-color:#e4e4e4 !important;
    display: ${({ $span, $grouping }) => (($grouping === 'individual' && ($span === 'day' || $span === 'week'))) ? 'flex' : 'none'};
    position:sticky;
    top:0;
    z-index:12;
  }

  /* Sticky Gutter */
  .rbc-time-gutter.rbc-time-column {
    position: sticky !important;
    left: 0;
    background-color: #e4e4e4;
    z-index: 10;
    border-right: 1px solid #bbb;
    min-height:100% !important;
    max-height:100% !important;
  }


  .rbc-label.rbc-time-header-gutter {
    position: sticky;
    top: 0;
    left: 0;
    background:#e4e4e4;
    border-right:1px solid #bbb;
  }

    
  &:not(.row-mode) .rbc-month-view .rbc-row.rbc-month-header {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .rbc-timeslot-group {

  }

  .rbc-time-header { /* IMPORTANT for group daily */
    overflow: ${({ $span, $grouping }) => (($grouping !== 'individual' && ($span === 'day'))) ? 'unset' : 'hidden'};
  }
  


  /* ===========================
     Day/Cell Styles
  =========================== */
  .rbc-day-slot {
    max-width: 100%;
  }

  .rbc-row-content {
    height: 100%;
    max-height: none;
  }

  .rbc-row-bg {
    height: 100%;
  }

  .rbc-off-range-bg {
    background: transparent;
  }

  .rbc-button-link {
    width:100%;
    pointer-events:none !important;
  }

  .rbc-row-segment {
    padding:1px 1px;
  }

  .date-is-today {
    background:red;
    color:white;
    line-height:1;
    font-size:12px;
    padding:6px;
    border-radius:50%;
    display: inline-block;
    margin-top:2px;
  }

  .date-is-today-header {
    color:red;
  }

  /* ===========================
     Event Styles
  =========================== */
  .rbc-event {
    transition: transform 0.2s ease-out, box-shadow 0.2s ease-out !important;

    &:hover {
      transform: translateY(-3px) !important;
      box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.2) !important;
    }
  }

  .rbc-event-content {
  height: 100%;

    > div {
      border-radius: 0 !important;
    }
  }

  .rbc-event-label {
    display: none;
  }

  /* ===========================
     Agenda View
  =========================== */
  .rbc-agenda-table {
    border: none !important;

    thead {
      display: none;
    }
  }

  .rbc-agenda-time-cell {
    display: none;
  }

  .rbc-agenda-content {
    background: white;
    border-radius: 6px;
    margin: 1rem;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

    .rbc-agenda-table tr {
      background: none !important;
      border-radius: unset !important;
    }
  }

  .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td {
    border: none !important;
    padding-left: 1px !important;
  }

  /* ===========================
     Utility Classes
  =========================== */
  .rbc-current-time-indicator {
    height: 1px;
    background-color: unset;
    border-bottom: 2px solid red;
    z-index: 9;
  }

  .rbc-overflowing {
    margin-right: 0 !important;
    overflow: hidden !important;
  }

  .rbc-show-more {
    font-size: 12px;
    padding-left: 5px;
    margin-top: 2px;
  }

  /* Fade animation */
  .fade-in {
    opacity: 0;
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &.visible {
      opacity: 1;
    }
  }

  .rbc-slot-selection {
    background-color:rgba(0, 0, 0, 0.05) !important;
    color:red !important;
    font-size:14px !important;
    font-weight:bold !important;
    border: 1px dashed rgba(0, 0, 0, 0.5) !important;
  }



  /* ===========================
     Additional UI Elements
  =========================== */
  .MuiButton-root {
    width: 100%;
  }

  .label-date {
    display: none;
  }

  .back-next-buttons {
    display: flex;
    justify-content: space-between;

    .btn-current {
      display: flex;
      align-items: center;
    }
  }
`;

